"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type EntityPreviewCardFocusContextValue = {
  focusedCardKey: string | null;
  registerCard: (cardKey: string) => void;
  unregisterCard: (cardKey: string) => void;
  focusCard: (cardKey: string) => void;
};

const EntityPreviewCardFocusContext = createContext<EntityPreviewCardFocusContextValue | null>(null);

export function EntityPreviewCardFocusProvider({ children }: { children: React.ReactNode }) {
  const [focusedCardKey, setFocusedCardKey] = useState<string | null>(null);

  const registerCard = useCallback((cardKey: string) => {
    setFocusedCardKey(cardKey);
  }, []);

  const unregisterCard = useCallback((cardKey: string) => {
    setFocusedCardKey((current) => (current === cardKey ? null : current));
  }, []);

  const focusCard = useCallback((cardKey: string) => {
    setFocusedCardKey(cardKey);
  }, []);

  const value = useMemo(
    () => ({
      focusedCardKey,
      registerCard,
      unregisterCard,
      focusCard,
    }),
    [focusedCardKey, registerCard, unregisterCard, focusCard],
  );

  return (
    <EntityPreviewCardFocusContext.Provider value={value}>{children}</EntityPreviewCardFocusContext.Provider>
  );
}

export function useEntityPreviewCardFocus(cardKey: string): {
  isExpanded: boolean;
  expand: () => void;
} {
  const context = useContext(EntityPreviewCardFocusContext);
  const registerCard = context?.registerCard;
  const unregisterCard = context?.unregisterCard;
  const focusCard = context?.focusCard;
  const isExpanded = context ? context.focusedCardKey === cardKey : true;

  const expand = useCallback(() => {
    focusCard?.(cardKey);
  }, [cardKey, focusCard]);

  useEffect(() => {
    registerCard?.(cardKey);
    return () => {
      unregisterCard?.(cardKey);
    };
  }, [cardKey, registerCard, unregisterCard]);

  return { isExpanded, expand };
}
